import React, { useState } from 'react';
import { 
  FileCheck2, ShieldCheck, Download, Copy, Check, 
  AlertCircle, FileText, ArrowLeft, RefreshCw, 
  Sparkles, ShieldAlert, CheckCircle, Scale, Database, Globe, Printer,
  Building, User, Hash, Layers, ListChecks, FileSpreadsheet, Eye, Send
} from 'lucide-react';
import TelegramExportButton from './TelegramExportButton';
import MarkdownRenderer from './MarkdownRenderer';

interface TenderPackageGeneratorProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const DEMO_PARTICIPANT = {
  edrpou: "43928172",
  name: "ТОВАРИСТВО З ОБМЕЖЕНОЮ ВІДПОВІДАЛЬНІСТЮ «ДІП РІСЕРЧ ХАБ ЮКРЕЙН»",
  shortName: "ТОВ «ДІП РІСЕРЧ ХАБ»",
  legalForm: "ТОВ",
  directorName: "Банах Андрій Степанович",
  directorPosition: "Директор",
  directorActingBasis: "Статут товариства (нова редакція)",
  address: "01010, м. Київ, вул. Князів Острозьких (Московська), буд. 8, офіс 412",
  email: "bass776@ukr.net",
  phone: "+38 (063) 325-38-48",
  iban: "UA843052990000026001234567890",
  bankName: "АТ КБ «ПРИВАТБАНК»",
  taxSystem: "Платник податку на прибуток на загальних підставах та платник ПДВ 20%",
  vatNumber: "439281726591"
};

const DEMO_CUSTOMER = {
  edrpou: "04052319",
  name: "Управління цифрової трансформації та інформаційних технологій виконавчого комітету",
  address: "01001, м. Київ, вул. Хрещатик, буд. 36",
  contactPerson: "Уповноважена особа з публічних закупівель"
};

const DEMO_PROCUREMENT = {
  tenderId: "UA-2026-11-05-001984-a",
  procedureType: "Відкриті торги з особливостями (Постанова КМУ № 1178)",
  subject: "Послуги з впровадження та технічної підтримки хмарної системи геопросторової аналітики та моніторингу надзвичайних ситуацій",
  dk021Code: "72260000-5",
  dk021Name: "Послуги, пов'язані з програмним забезпеченням (Software-related services)",
  expectedPriceUah: 1250000.00,
  bidPriceWithoutVat: 980000.00,
  bidVatAmount: 196000.00,
  bidPriceWithVat: 1176000.00,
  bidPriceInWords: "один мільйон сто сімдесят шість тисяч гривень 00 копійок",
  deliveryPeriod: "до 25 грудня 2026 року",
  deliveryAddress: "01001, м. Київ, вул. Хрещатик, буд. 36 (хмарна інфраструктура замовника)",
  paymentTerms: "100% післяоплата протягом 10 (десяти) банківських днів з дати підписання Акта наданих послуг",
  bidValidityDays: 90
};

export default function TenderPackageGeneratorView({
  language,
  onBack,
  onGoHome,
  useEnterpriseContext,
  enterpriseName
}: TenderPackageGeneratorProps) {
  const isUk = language === 'uk';

  // Navigation tab inside generator
  const [activeTab, setActiveTab] = useState<'form' | 'sanctions' | 'preview'>('form');

  // Form states
  const [participant, setParticipant] = useState(DEMO_PARTICIPANT);
  const [customer, setCustomer] = useState(DEMO_CUSTOMER);
  const [procurement, setProcurement] = useState(DEMO_PROCUREMENT);

  // Statuses
  const [isCheckingSanctions, setIsCheckingSanctions] = useState(false);
  const [isGeneratingPackage, setIsGeneratingPackage] = useState(false);
  const [isExportingDocx, setIsExportingDocx] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Results
  const [sanctionResult, setSanctionResult] = useState<any | null>(null);
  const [generatedPackage, setGeneratedPackage] = useState<any | null>(null);
  const [selectedDocIndex, setSelectedDocIndex] = useState<number>(-1); // -1 = consolidated suite
  const [copiedDoc, setCopiedDoc] = useState(false);

  // 1. Run automated sanctions check
  const handleCheckSanctions = async () => {
    setIsCheckingSanctions(true);
    setError(null);
    try {
      const response = await fetch('/api/tender-package/check-sanctions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ participant, procurement })
      });
      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'Помилка автоматичної перевірки санкцій.');
      }
      setSanctionResult(data.checkResult);
      setActiveTab('sanctions');
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Не вдалося виконати перевірку санкційних реєстрів.');
    } finally {
      setIsCheckingSanctions(false);
    }
  };

  // 2. Generate full compliant tender package
  const handleGeneratePackage = async () => {
    setIsGeneratingPackage(true);
    setError(null);
    try {
      const response = await fetch('/api/tender-package/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          participant,
          customer,
          procurement,
          qualification: {
            equipment: [],
            staff: [],
            similarContracts: []
          }
        })
      });
      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'Помилка формування пакета тендерної пропозиції.');
      }
      setGeneratedPackage(data.package);
      setSanctionResult(data.package.sanctionCheck);
      setActiveTab('preview');
      setSelectedDocIndex(-1); // show all
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Сталася помилка при генерації документів.');
    } finally {
      setIsGeneratingPackage(false);
    }
  };

  // 3. Export to Word (.docx)
  const handleExportDocx = async (textToExport: string, title: string) => {
    setIsExportingDocx(true);
    try {
      const response = await fetch('/api/tender-package/export-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          markdownText: textToExport,
          fileName: title
        })
      });

      if (!response.ok) {
        throw new Error('Помилка генерації Word документа.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(err);
      alert(err.message || 'Не вдалося експортувати документ у DOCX.');
    } finally {
      setIsExportingDocx(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedDoc(true);
    setTimeout(() => setCopiedDoc(false), 2000);
  };

  const currentPreviewMarkdown = generatedPackage
    ? (selectedDocIndex === -1 
        ? generatedPackage.consolidatedMarkdown 
        : generatedPackage.documents[selectedDocIndex]?.markdownContent || '')
    : '';

  const currentPreviewTitle = generatedPackage
    ? (selectedDocIndex === -1
        ? `Тендерний_Пакет_${procurement.tenderId}_ПКМУ1178`
        : `${generatedPackage.documents[selectedDocIndex]?.docTitleUk.replace(/\s+/g, '_')}_${procurement.tenderId}`)
    : 'Тендерний_пакет';

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16 animate-fadeIn">
      {/* Top Header Card */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />
        <div className="flex flex-wrap items-center justify-between gap-4 relative z-10">
          <div className="flex items-center space-x-3">
            <button
              onClick={onGoHome || onBack}
              className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition-colors border border-slate-700 cursor-pointer"
              title={isUk ? "Повернутися назад" : "Go Back"}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-12 h-12 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center justify-center text-emerald-400 shadow-inner">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl sm:text-2xl font-black text-white tracking-tight">
                  {isUk ? "Генератор тендерного пакета за Постановою № 1178" : "Tender Package Generator (Res. #1178)"}
                </h1>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  ПКМУ № 1178 • ДСТУ 4163:2020
                </span>
              </div>
              <p className="text-xs sm:text-sm text-slate-400 mt-0.5">
                {isUk 
                  ? "Автоматична перевірка 8 санкційних реєстрів та формування 100% юридично бездоганного пакета документів учасника" 
                  : "Automated sanctions registry audit and instant 10-document tender bid package drafting"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-emerald-400/80 bg-emerald-950/60 border border-emerald-800/60 px-3 py-1.5 rounded-xl flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span>Deep Research Hub</span>
            </span>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center gap-2 mt-6 pt-4 border-t border-slate-800/80 flex-wrap">
          <button
            onClick={() => setActiveTab('form')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === 'form'
                ? 'bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-500/20'
                : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700 border border-slate-700/60'
            }`}
          >
            <FileSpreadsheet className="w-4 h-4" />
            <span>1. Реквізити та закупівля</span>
          </button>

          <button
            onClick={() => setActiveTab('sanctions')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
              activeTab === 'sanctions'
                ? 'bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-500/20'
                : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700 border border-slate-700/60'
            }`}
          >
            <ShieldAlert className="w-4 h-4" />
            <span>2. Радар санкційних реєстрів</span>
            {sanctionResult && (
              <span className="px-1.5 py-0.2 bg-emerald-900 text-emerald-200 text-[10px] rounded-full border border-emerald-500/40">
                100%
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('preview')}
            disabled={!generatedPackage}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
              activeTab === 'preview'
                ? 'bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-500/20'
                : 'bg-slate-800/80 text-slate-300 hover:bg-slate-700 border border-slate-700/60'
            }`}
          >
            <Eye className="w-4 h-4" />
            <span>3. Сформований пакет (10 документів)</span>
            {generatedPackage && (
              <span className="px-1.5 py-0.2 bg-emerald-900 text-emerald-200 text-[10px] rounded-full border border-emerald-500/40">
                10
              </span>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-xl text-rose-300 text-xs flex items-center gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0 text-rose-400" />
          <span>{error}</span>
        </div>
      )}

      {/* TAB 1: FORM INPUTS */}
      {activeTab === 'form' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column: Participant Details */}
          <div className="lg:col-span-6 space-y-6">
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-emerald-400 font-mono uppercase tracking-wider flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  <span>1. Реквізити Учасника (Постачальника)</span>
                </h3>
                <button
                  type="button"
                  onClick={() => setParticipant(DEMO_PARTICIPANT)}
                  className="text-xs text-amber-400 hover:text-amber-300 underline cursor-pointer"
                >
                  Заповнити демо-даними
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-400 font-mono block mb-1">Код ЄДРПОУ або РНОКПП (8 або 10 цифр):</label>
                  <input
                    type="text"
                    value={participant.edrpou}
                    onChange={(e) => setParticipant({ ...participant, edrpou: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs font-mono text-slate-100 focus:outline-none focus:border-emerald-400"
                    placeholder="43928172"
                  />
                </div>

                <div>
                  <label className="text-xs text-slate-400 font-mono block mb-1">Повне найменування юридичної особи або ПІБ ФОП:</label>
                  <input
                    type="text"
                    value={participant.name}
                    onChange={(e) => setParticipant({ ...participant, name: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-100 focus:outline-none focus:border-emerald-400"
                    placeholder="ТОВ «Назва Компанії»"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 font-mono block mb-1">ПІБ керівника / підписанта:</label>
                    <input
                      type="text"
                      value={participant.directorName}
                      onChange={(e) => setParticipant({ ...participant, directorName: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-100 focus:outline-none focus:border-emerald-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 font-mono block mb-1">Посада керівника:</label>
                    <input
                      type="text"
                      value={participant.directorPosition}
                      onChange={(e) => setParticipant({ ...participant, directorPosition: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-100 focus:outline-none focus:border-emerald-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-slate-400 font-mono block mb-1">Діє на підставі:</label>
                  <input
                    type="text"
                    value={participant.directorActingBasis}
                    onChange={(e) => setParticipant({ ...participant, directorActingBasis: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-100 focus:outline-none focus:border-emerald-400"
                  />
                </div>

                <div>
                  <label className="text-xs text-slate-400 font-mono block mb-1">Юридична адреса:</label>
                  <input
                    type="text"
                    value={participant.address}
                    onChange={(e) => setParticipant({ ...participant, address: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-100 focus:outline-none focus:border-emerald-400"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 font-mono block mb-1">IBAN рахунок:</label>
                    <input
                      type="text"
                      value={participant.iban}
                      onChange={(e) => setParticipant({ ...participant, iban: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs font-mono text-slate-100 focus:outline-none focus:border-emerald-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 font-mono block mb-1">Обслуговуючий банк:</label>
                    <input
                      type="text"
                      value={participant.bankName}
                      onChange={(e) => setParticipant({ ...participant, bankName: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-100 focus:outline-none focus:border-emerald-400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 font-mono block mb-1">Телефон:</label>
                    <input
                      type="text"
                      value={participant.phone}
                      onChange={(e) => setParticipant({ ...participant, phone: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs font-mono text-slate-100 focus:outline-none focus:border-emerald-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 font-mono block mb-1">Email:</label>
                    <input
                      type="text"
                      value={participant.email}
                      onChange={(e) => setParticipant({ ...participant, email: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs font-mono text-slate-100 focus:outline-none focus:border-emerald-400"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Action Button */}
            <div className="p-5 bg-gradient-to-r from-emerald-950/40 via-slate-900 to-slate-900 border border-emerald-500/30 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h4 className="text-xs font-bold text-emerald-400 font-mono uppercase tracking-wider">
                  Автоматичний аудит санкцій:
                </h4>
                <p className="text-[11px] text-slate-400">
                  Миттєва перевірка за РНБО, ПКМУ 187, НАЗК, АМКУ, ДПС та ЄДРСР перед поданням.
                </p>
              </div>
              <button
                type="button"
                onClick={handleCheckSanctions}
                disabled={isCheckingSanctions}
                className="w-full sm:w-auto px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 cursor-pointer disabled:opacity-50"
              >
                {isCheckingSanctions ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
                <span>{isCheckingSanctions ? "Перевірка..." : "🔍 Перевірити санкції"}</span>
              </button>
            </div>
          </div>

          {/* Right Column: Customer & Procurement Details */}
          <div className="lg:col-span-6 space-y-6">
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-amber-400 font-mono uppercase tracking-wider flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  <span>2. Реквізити Замовника (Організатора)</span>
                </h3>
                <button
                  type="button"
                  onClick={() => setCustomer(DEMO_CUSTOMER)}
                  className="text-xs text-amber-400 hover:text-amber-300 underline cursor-pointer"
                >
                  Заповнити демо
                </button>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <label className="text-xs text-slate-400 font-mono block mb-1">ЄДРПОУ Замовника:</label>
                    <input
                      type="text"
                      value={customer.edrpou}
                      onChange={(e) => setCustomer({ ...customer, edrpou: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs font-mono text-slate-100 focus:outline-none focus:border-amber-400"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-slate-400 font-mono block mb-1">Повне найменування Замовника:</label>
                    <input
                      type="text"
                      value={customer.name}
                      onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-100 focus:outline-none focus:border-amber-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-slate-400 font-mono block mb-1">Адреса Замовника:</label>
                  <input
                    type="text"
                    value={customer.address}
                    onChange={(e) => setCustomer({ ...customer, address: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-100 focus:outline-none focus:border-amber-400"
                  />
                </div>
              </div>
            </div>

            {/* Procurement Parameters */}
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 space-y-4">
              <h3 className="text-sm font-bold text-sky-400 font-mono uppercase tracking-wider flex items-center gap-2">
                <Layers className="w-4 h-4" />
                <span>3. Параметри закупівлі (Prozorro)</span>
              </h3>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 font-mono block mb-1">Ідентифікатор закупівлі (Prozorro ID):</label>
                    <input
                      type="text"
                      value={procurement.tenderId}
                      onChange={(e) => setProcurement({ ...procurement, tenderId: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs font-mono text-slate-100 focus:outline-none focus:border-sky-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 font-mono block mb-1">Код ДК 021:2015:</label>
                    <input
                      type="text"
                      value={procurement.dk021Code}
                      onChange={(e) => setProcurement({ ...procurement, dk021Code: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs font-mono text-slate-100 focus:outline-none focus:border-sky-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-slate-400 font-mono block mb-1">Предмет закупівлі:</label>
                  <textarea
                    rows={2}
                    value={procurement.subject}
                    onChange={(e) => setProcurement({ ...procurement, subject: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-100 focus:outline-none focus:border-sky-400"
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 font-mono block mb-1">Ціна без ПДВ (грн):</label>
                    <input
                      type="number"
                      value={procurement.bidPriceWithoutVat}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        const vat = Math.round(val * 0.20 * 100) / 100;
                        setProcurement({
                          ...procurement,
                          bidPriceWithoutVat: val,
                          bidVatAmount: vat,
                          bidPriceWithVat: val + vat
                        });
                      }}
                      className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs font-mono text-slate-100 focus:outline-none focus:border-sky-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 font-mono block mb-1">ПДВ 20% (грн):</label>
                    <input
                      type="number"
                      value={procurement.bidVatAmount}
                      readOnly
                      className="w-full px-3.5 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs font-mono text-slate-400 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 font-mono block mb-1">Загальна ціна з ПДВ:</label>
                    <input
                      type="number"
                      value={procurement.bidPriceWithVat}
                      readOnly
                      className="w-full px-3.5 py-2 bg-slate-900 border border-slate-800 rounded-xl text-xs font-mono font-bold text-emerald-400 cursor-not-allowed"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-slate-400 font-mono block mb-1">Сума прописом:</label>
                  <input
                    type="text"
                    value={procurement.bidPriceInWords}
                    onChange={(e) => setProcurement({ ...procurement, bidPriceInWords: e.target.value })}
                    className="w-full px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-100 focus:outline-none focus:border-sky-400"
                  />
                </div>
              </div>
            </div>

            {/* Master Generate Button */}
            <button
              type="button"
              onClick={handleGeneratePackage}
              disabled={isGeneratingPackage}
              className="w-full py-4 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 hover:opacity-95 text-slate-950 font-black text-sm uppercase tracking-wider rounded-2xl transition-all flex items-center justify-center gap-3 shadow-xl shadow-emerald-500/20 cursor-pointer disabled:opacity-50"
            >
              {isGeneratingPackage ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
              <span>{isGeneratingPackage ? "Генерація тендерного пакета..." : "🚀 ЗГЕНЕРУВАТИ ПОВНИЙ ТЕНДЕРНИЙ ПАКЕТ ЗА ПКМУ № 1178 (10 ДОКУМЕНТІВ)"}</span>
            </button>
          </div>
        </div>
      )}

      {/* TAB 2: SANCTIONS RADAR */}
      {activeTab === 'sanctions' && (
        <div className="space-y-6">
          <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6">
            <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-slate-800">
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                  <span>Протокол автоматизованої верифікації санкційних та регуляторних реєстрів</span>
                </h3>
                <p className="text-xs text-slate-400 mt-1">
                  Аудит за Постановою КМУ № 1178 (пункти 44 та 47), Постановою КМУ № 187 та ЗУ «Про публічні закупівлі».
                </p>
              </div>

              {sanctionResult && (
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <span className="text-[10px] text-slate-400 font-mono block">КРИПТОГРАФІЧНИЙ ХЕШ (SHA-256):</span>
                    <span className="text-xs font-mono text-emerald-400 font-bold">
                      {sanctionResult.auditHashSha256.substring(0, 20)}...
                    </span>
                  </div>
                  <div className="w-12 h-12 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center justify-center text-emerald-400 font-black text-sm">
                    {sanctionResult.complianceScore}%
                  </div>
                </div>
              )}
            </div>

            {/* Registries Grid */}
            {sanctionResult ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                {Object.values(sanctionResult.registries).map((reg: any, idx: number) => {
                  const isClean = reg.status === 'CLEAN';
                  return (
                    <div
                      key={idx}
                      className={`p-4 rounded-xl border transition-all ${
                        isClean 
                          ? 'bg-slate-950/80 border-slate-800 hover:border-emerald-500/40' 
                          : 'bg-rose-950/20 border-rose-500/40'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            {isClean ? (
                              <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
                            )}
                            <h4 className="text-xs font-bold text-slate-200">
                              {reg.name}
                            </h4>
                          </div>
                          <p className="text-[11px] text-slate-400">
                            {reg.legalBasis}
                          </p>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold flex-shrink-0 ${
                          isClean ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                        }`}>
                          {isClean ? 'ВІДСУТНІ (CLEAN)' : 'ПОРУШЕННЯ'}
                        </span>
                      </div>

                      <p className="text-xs text-slate-300 mt-2.5 pt-2 border-t border-slate-900 leading-relaxed">
                        {reg.details}
                      </p>

                      <div className="mt-2 text-[10px] font-mono text-slate-500 flex items-center gap-1">
                        <Globe className="w-3 h-3 text-slate-600" />
                        <span>{reg.checkedSource}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 space-y-4">
                <ShieldAlert className="w-12 h-12 text-slate-600 mx-auto" />
                <p className="text-sm text-slate-400">
                  Перевірка ще не проводилася. Натисніть кнопку нижче для запуску онлайн-аудиту.
                </p>
                <button
                  type="button"
                  onClick={handleCheckSanctions}
                  disabled={isCheckingSanctions}
                  className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs uppercase tracking-wider rounded-xl transition-all inline-flex items-center gap-2 cursor-pointer"
                >
                  {isCheckingSanctions ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                  <span>Запустити повний аудит санкцій</span>
                </button>
              </div>
            )}

            {sanctionResult && (
              <div className="mt-6 p-4 bg-emerald-950/30 border border-emerald-500/30 rounded-xl flex items-center justify-between flex-wrap gap-3">
                <div className="text-xs text-emerald-300">
                  {sanctionResult.summaryRecommendation}
                </div>
                <button
                  type="button"
                  onClick={handleGeneratePackage}
                  disabled={isGeneratingPackage}
                  className="px-5 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center gap-2 cursor-pointer"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>Перейти до генерації пакета документів</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 3: PREVIEW & EXPORT (10 DOCUMENTS) */}
      {activeTab === 'preview' && generatedPackage && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Document Selector Sidebar */}
          <div className="lg:col-span-4 space-y-3">
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-4 space-y-3">
              <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                <h3 className="text-xs font-mono font-bold text-emerald-400 uppercase tracking-wider flex items-center gap-2">
                  <ListChecks className="w-4 h-4" />
                  <span>Склад тендерного пакета</span>
                </h3>
                <span className="text-[11px] text-slate-400 font-mono">10 документів</span>
              </div>

              {/* View All Button */}
              <button
                type="button"
                onClick={() => setSelectedDocIndex(-1)}
                className={`w-full text-left p-3 rounded-xl text-xs font-bold transition-all flex items-center justify-between cursor-pointer border ${
                  selectedDocIndex === -1
                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 shadow-inner'
                    : 'bg-slate-950/60 text-slate-300 border-slate-800/80 hover:bg-slate-800/60'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-emerald-400" />
                  <span>📚 Повний консолідований пакет (Всі 10 актів)</span>
                </div>
              </button>

              {/* Individual Documents List */}
              <div className="space-y-1.5 max-h-[580px] overflow-y-auto pr-1">
                {generatedPackage.documents.map((doc: any, idx: number) => (
                  <button
                    key={doc.docId}
                    type="button"
                    onClick={() => setSelectedDocIndex(idx)}
                    className={`w-full text-left p-2.5 rounded-xl text-xs transition-all flex items-start justify-between cursor-pointer border ${
                      selectedDocIndex === idx
                        ? 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40 font-bold'
                        : 'bg-slate-950/40 text-slate-400 border-slate-800/60 hover:bg-slate-800/40 hover:text-slate-200'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-slate-800 text-[10px] font-mono flex items-center justify-center text-slate-300 flex-shrink-0 mt-0.5">
                        {idx + 1}
                      </span>
                      <div className="space-y-0.5">
                        <div className="leading-snug">{doc.docTitleUk}</div>
                        <div className="text-[10px] font-mono text-slate-500">{doc.docNumber}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Export Panel */}
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-4 space-y-3">
              <h4 className="text-xs font-mono font-bold text-slate-300 uppercase tracking-wider">
                Швидкий експорт:
              </h4>

              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => handleExportDocx(currentPreviewMarkdown, currentPreviewTitle)}
                  disabled={isExportingDocx}
                  className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-500/20 cursor-pointer disabled:opacity-50"
                >
                  {isExportingDocx ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  <span>Завантажити у Word (.docx) ДСТУ 4163</span>
                </button>

                <TelegramExportButton
                  text={currentPreviewMarkdown}
                  title={currentPreviewTitle}
                  language={language}
                />
              </div>
            </div>
          </div>

          {/* Document Content Viewer */}
          <div className="lg:col-span-8 space-y-4">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-slate-800">
                <div className="space-y-1">
                  <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400">
                    {selectedDocIndex === -1 ? 'Консолідований пакет' : `Документ № ${selectedDocIndex + 1}`}
                  </span>
                  <h3 className="text-sm font-bold text-white">
                    {selectedDocIndex === -1 
                      ? 'Повний пакет документів тендерної пропозиції за ПКМУ № 1178' 
                      : generatedPackage.documents[selectedDocIndex]?.docTitleUk}
                  </h3>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleCopy(currentPreviewMarkdown)}
                    className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition-colors border border-slate-700 text-xs flex items-center gap-1.5 cursor-pointer"
                    title="Копіювати текст"
                  >
                    {copiedDoc ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    <span>{copiedDoc ? "Скопійовано" : "Копіювати"}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => window.print()}
                    className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition-colors border border-slate-700 text-xs flex items-center gap-1.5 cursor-pointer"
                    title="Друкувати"
                  >
                    <Printer className="w-4 h-4" />
                    <span>Друк</span>
                  </button>
                </div>
              </div>

              {/* Markdown Content */}
              <div className="mt-6 prose prose-invert max-w-none text-slate-300 text-xs sm:text-sm leading-relaxed overflow-x-auto">
                <MarkdownRenderer content={currentPreviewMarkdown} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
